/**
 * V70 타자왕 — React hook (engine 바인딩)
 *
 * - requestAnimationFrame 60fps 루프
 * - 포커스/탭 변경 시에도 서버 시간 기준으로 계속 (일시정지 없음)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AcidRainEngine, SPAWN_ALGORITHM_VERSION } from './engine';
import { WORD_DICTIONARY_VERSION } from './wordDictionary';
import type { AcidRainSettings, GameResult, GameSnapshot } from './types';
import {
  convertEngineEventsToServerInputEvents,
  type AcidRainServerInputEvent as _ConvertedServerInputEvent,
} from './eventConverter';

interface UseAcidRainEngineOptions {
  settings: AcidRainSettings;
  sessionId: string;
  seed: string;
  startedAtServerMs: number;
  fieldHeightPx?: number;
  onEnd?: (result: GameResult) => void;
}

/**
 * Phase 3-C.1 — 서버 재계산용 입력 이벤트 shape re-export (eventConverter 에서 정의).
 */
export type AcidRainServerInputEvent = _ConvertedServerInputEvent;

interface UseAcidRainEngineResult {
  snapshot: GameSnapshot | null;
  isRunning: boolean;
  startAfterCountdown: () => void;
  stopEarly: () => GameResult | null;
  setInput: (v: string) => void;
  onCompositionStart: () => void;
  onCompositionEnd: (text: string) => void;
  submit: () => { matched: boolean; hadInput: boolean };
  clearBuffer: () => void;
  /**
   * Phase 3-C.1 결함 A 수정 — 서버 재계산용 입력 이벤트 수집.
   * engine.getEvents() 의 word_cleared/word_missed GameEvent 를 ServerInputEvent[] 로 변환.
   * seq 는 호출 시점 index 순차 (0, 1, 2, ...).
   * missed 이벤트는 combo=0 (서버 재계산 담당 — FE combo 리셋만).
   */
  getInputEvents: () => AcidRainServerInputEvent[];
}

export function useAcidRainEngine(opts: UseAcidRainEngineOptions): UseAcidRainEngineResult {
  const engineRef = useRef<AcidRainEngine | null>(null);
  const rafRef = useRef<number | null>(null);
  const [snapshot, setSnapshot] = useState<GameSnapshot | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  if (!engineRef.current) {
    engineRef.current = new AcidRainEngine({
      settings: opts.settings,
      sessionId: opts.sessionId,
      seed: opts.seed,
      startedAtServerMs: opts.startedAtServerMs,
      fieldHeightPx: opts.fieldHeightPx,
    });
    setSnapshot(engineRef.current.snapshot());
  }

  const loop = useCallback(
    (perfNow: number) => {
      const engine = engineRef.current;
      if (!engine) return;
      const snap = engine.tick(perfNow);
      setSnapshot(snap);
      if (snap.status === 'ended') {
        setIsRunning(false);
        rafRef.current = null;
        if (opts.onEnd) {
          const events = engine.getEvents();
          const result: GameResult = {
            sessionId: opts.sessionId,
            startedAt: opts.startedAtServerMs,
            endedAt: opts.startedAtServerMs + snap.elapsedMs,
            elapsedMs: snap.elapsedMs,
            clientReportedScore: snap.score,
            cleared: snap.cleared,
            missed: snap.missed,
            maxCombo: snap.maxCombo,
            reason: events[events.length - 1]?.payload?.reason as GameResult['reason'] ?? 'time_up',
            dictionaryVersion: WORD_DICTIONARY_VERSION,
            spawnAlgorithmVersion: SPAWN_ALGORITHM_VERSION,
          };
          opts.onEnd(result);
        }
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    },
    [opts]
  );

  const startAfterCountdown = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.start(performance.now());
    setIsRunning(true);
    rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  const stopEarly = useCallback((): GameResult | null => {
    const engine = engineRef.current;
    if (!engine) return null;
    const snap = engine.snapshot();
    const result = engine.end('user_quit', snap.elapsedMs);
    setSnapshot(engine.snapshot());
    setIsRunning(false);
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    return result;
  }, []);

  const setInput = useCallback((v: string) => {
    engineRef.current?.onInput(v);
  }, []);

  const onCompositionStart = useCallback(() => {
    engineRef.current?.onCompositionStart();
  }, []);

  const onCompositionEnd = useCallback((text: string) => {
    engineRef.current?.onCompositionEnd(text);
  }, []);

  const submit = useCallback((): { matched: boolean; hadInput: boolean } => {
    return engineRef.current?.submit() ?? { matched: false, hadInput: false };
  }, []);

  const clearBuffer = useCallback(() => {
    engineRef.current?.clearBuffer();
  }, []);

  // Phase 3-C.1 결함 A 수정 — 서버 재계산용 입력 이벤트 수집. 변환 로직은 eventConverter 로 분리.
  const getInputEvents = useCallback((): AcidRainServerInputEvent[] => {
    const engine = engineRef.current;
    if (!engine) return [];
    return convertEngineEventsToServerInputEvents(engine.getEvents());
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  return {
    snapshot,
    isRunning,
    startAfterCountdown,
    stopEarly,
    setInput,
    onCompositionStart,
    onCompositionEnd,
    submit,
    clearBuffer,
    getInputEvents,
  };
}
